import { html } from '../html.js';
import type { VNode } from '../html.js';

/**
 * Copy-to-clipboard without a hook.
 *
 * The console renders by calling view functions directly (the same pattern
 * apps/web uses), so there are no component instances to hang `useState` on.
 * "Which button last succeeded" therefore lives in the store like everything
 * else, and this file is just the button plus the platform dance.
 */

export interface CopyProps {
  /** Stable identity, so only the button that was pressed says "copied". */
  id: string;
  text: string;
  copied: string | null;
  onCopy: (id: string, text: string) => void;
  label?: string;
  /** Announced to screen readers; the visible label is intentionally terse. */
  describe: string;
}

export function CopyButton(props: CopyProps): VNode {
  const done = props.copied === props.id;
  return html`
    <button
      type="button"
      class=${done ? 'copy copy--done' : 'copy'}
      onClick=${() => props.onCopy(props.id, props.text)}
      aria-label=${done ? `Copied ${props.describe}` : `Copy ${props.describe}`}
    >
      ${done ? '✓ Copied' : (props.label ?? 'Copy')}
    </button>
  `;
}

/**
 * `navigator.clipboard` is unavailable on plain-http origins, which is exactly
 * where an operator will first open a self-hosted console. The textarea path is
 * deprecated, not dead, and a copy button that silently does nothing is worse
 * than a deprecated API call.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}
